import { createHostelSchema, createRoomSchema, HOSTEL_RESIDENTS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';
import { useCurrency, type Hostel, type Message, type Room } from './shared';

const blankHostel = { name: '', residents: 'mixed' };
const blankRoom = { number: '', floor: '', beds: '', fee: '' };

export function RoomsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const currency = useCurrency(schoolId);
  const managing = can('hostel.manage', schoolId);
  const [hostelForm, setHostelForm] = useState(blankHostel);
  const [roomForm, setRoomForm] = useState(blankRoom);
  const [open, setOpen] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const hostels = useQuery({
    queryKey: ['hostel', schoolId, 'hostels'],
    queryFn: () =>
      apiList<Hostel>(`/schools/${schoolId}/hostel/hostels`, { query: { pageSize: 100 } }),
  });
  const detail = useQuery({
    queryKey: ['hostel', schoolId, 'hostel', open],
    queryFn: () => api<{ rooms: Room[] }>(`/schools/${schoolId}/hostel/hostels/${open}`),
    enabled: Boolean(open),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hostel', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const addHostel = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/hostel/hostels`, { method: 'POST', body }),
    onSuccess: () => {
      setHostelForm(blankHostel);
      setMessage({ kind: 'ok', text: t('ho.hostelAdded') });
      refresh();
    },
    onError: fail,
  });
  const addRoom = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/hostel/hostels/${open}/rooms`, { method: 'POST', body }),
    onSuccess: () => {
      setRoomForm(blankRoom);
      setMessage({ kind: 'ok', text: t('ho.roomAdded') });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('ho.tab.rooms')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {hostels.data?.data.length === 0 && <p className="text-slate-600">{t('ho.noHostels')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {hostels.data?.data.map((h) => (
          <li key={h.id} className="space-y-2 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <span className="font-medium">{h.name}</span> ·{' '}
                {t(`ho.residents.${h.residents}` as MessageKey)}
                {h.status === 'inactive' && (
                  <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">
                    {t('ho.closed')}
                  </span>
                )}
                {h.wardenName ? ` · ${t('ho.wardenIs', { name: h.wardenName })}` : ''}
              </span>
              <span className="flex items-center gap-3">
                <span>{t('ho.bedsTaken', { taken: h.occupied, total: h.beds })}</span>
                <button
                  type="button"
                  className={secondaryButton}
                  aria-expanded={open === h.id}
                  aria-label={`${t('ho.rooms')}: ${h.name}`}
                  onClick={() => setOpen(open === h.id ? '' : h.id)}
                >
                  {t('ho.rooms')} ({h.roomCount})
                </button>
              </span>
            </div>
            {open === h.id && (
              <div className="space-y-3 rounded bg-slate-50 p-3">
                {detail.data?.rooms.length === 0 && (
                  <p className="text-slate-600">{t('ho.noRooms')}</p>
                )}
                <ul className="space-y-1">
                  {detail.data?.rooms.map((r) => (
                    <li key={r.id} className="flex flex-wrap justify-between gap-2">
                      <span>
                        {t('ho.roomLabel', { number: r.roomNumber })}
                        {r.floor !== null ? ` · ${t('ho.floorLabel', { floor: r.floor })}` : ''}
                        {r.status === 'maintenance' ? ` · ${t('ho.maintenance')}` : ''}
                      </span>
                      <span>
                        {t('ho.bedsTaken', { taken: r.occupied, total: r.capacity })} ·{' '}
                        {t('ho.perMonth', { amount: formatMinor(r.monthlyFeeMinor, currency) })}
                      </span>
                    </li>
                  ))}
                </ul>
                {managing && (
                  <form
                    noValidate
                    aria-label={t('ho.addRoom')}
                    className="grid gap-3 sm:grid-cols-5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const feeMinor = parseMajor(roomForm.fee || '0', currency);
                      const parsed = createRoomSchema.safeParse({
                        roomNumber: roomForm.number,
                        floor: roomForm.floor === '' ? null : Number(roomForm.floor),
                        capacity: Number(roomForm.beds),
                        monthlyFeeMinor: feeMinor,
                      });
                      const next: Record<string, string> = {};
                      if (!parsed.success)
                        for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
                      setErrors(next);
                      if (!parsed.success) return;
                      setMessage(null);
                      addRoom.mutate(parsed.data);
                    }}
                  >
                    <TextField
                      label={t('ho.roomNumber')}
                      value={roomForm.number}
                      error={errors.roomNumber}
                      onChange={(e) => setRoomForm({ ...roomForm, number: e.target.value })}
                    />
                    <TextField
                      label={t('ho.floor')}
                      inputMode="numeric"
                      value={roomForm.floor}
                      error={errors.floor}
                      onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                    />
                    <TextField
                      label={t('ho.beds')}
                      inputMode="numeric"
                      value={roomForm.beds}
                      error={errors.capacity}
                      onChange={(e) => setRoomForm({ ...roomForm, beds: e.target.value })}
                    />
                    <TextField
                      label={t('ho.monthlyFee', { currency })}
                      inputMode="decimal"
                      value={roomForm.fee}
                      error={errors.monthlyFeeMinor}
                      onChange={(e) => setRoomForm({ ...roomForm, fee: e.target.value })}
                    />
                    <div className="flex items-end">
                      <button type="submit" className={primaryButton} disabled={addRoom.isPending}>
                        {t('ho.addRoom')}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {managing && (
        <form
          noValidate
          aria-label={t('ho.addHostel')}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createHostelSchema.safeParse(hostelForm);
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            addHostel.mutate(parsed.data);
          }}
        >
          <TextField
            label={t('ho.hostelName')}
            value={hostelForm.name}
            error={errors.name}
            onChange={(e) => setHostelForm({ ...hostelForm, name: e.target.value })}
          />
          <div>
            <label htmlFor="hostel-residents" className="block text-sm font-medium">
              {t('ho.takes')}
            </label>
            <select
              id="hostel-residents"
              className={selectClass}
              value={hostelForm.residents}
              onChange={(e) => setHostelForm({ ...hostelForm, residents: e.target.value })}
            >
              {HOSTEL_RESIDENTS.map((x) => (
                <option key={x} value={x}>
                  {t(`ho.residents.${x}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button type="submit" className={primaryButton} disabled={addHostel.isPending}>
              {t('ho.addHostel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
